import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { deliveryAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const DeliveryCalendar = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDeliveries();
  }, []);

  const loadDeliveries = async () => {
    try {
      const response = await deliveryAPI.getAll();
      setDeliveries(response.data.data || response.data);
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const getDeliveriesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return deliveries.filter((delivery) => {
      const deliveryDate = new Date(delivery.scheduled_date).toISOString().split('T')[0];
      return deliveryDate === dateStr;
    });
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dayDeliveries = getDeliveriesForDate(date);
      if (dayDeliveries.length > 0) {
        return (
          <div className="flex justify-center mt-1">
            <span className="inline-block w-2 h-2 bg-blue-600 rounded-full"></span>
          </div>
        );
      }
    }
    return null;
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const dayDeliveries = getDeliveriesForDate(date);
      if (dayDeliveries.length > 0) {
        return 'has-delivery';
      }
    }
    return null;
  };

  const selectedDeliveries = getDeliveriesForDate(selectedDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('deliveries.title')}</h2>
          <p className="text-gray-600 mt-1">{t('deliveries.subtitle')}</p>
        </div>
        <Link
          to="/deliveries"
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200"
        >
          {t('deliveries.listView')}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
          <style>{`
            .react-calendar {
              width: 100%;
              border: none;
              font-family: inherit;
            }
            .react-calendar__tile {
              padding: 1em 0.5em;
              border-radius: 0.5rem;
            }
            .react-calendar__tile--active {
              background: linear-gradient(to right, #2563eb, #9333ea);
              color: white;
            }
            .react-calendar__tile--now {
              background: #dbeafe;
            }
            .react-calendar__tile:enabled:hover {
              background: #f3f4f6;
            }
            .react-calendar__tile.has-delivery {
              background: #eff6ff;
              font-weight: 600;
            }
            .react-calendar__navigation button {
              font-size: 1rem;
              font-weight: 600;
              border-radius: 0.5rem;
            }
            .react-calendar__navigation button:enabled:hover {
              background: #f3f4f6;
            }
          `}</style>
          <Calendar
            onChange={setSelectedDate}
            value={selectedDate}
            tileContent={tileContent}
            tileClassName={tileClassName}
          />
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {t('deliveries.deliveriesOn')} {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </h3>

          {selectedDeliveries.length > 0 ? (
            <div className="space-y-4">
              {selectedDeliveries.map((delivery) => (
                <div key={delivery.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900">
                        Order #{delivery.sales_order?.order_number}
                      </p>
                      <p className="text-sm text-gray-600">{delivery.sales_order?.customer?.name}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      delivery.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                      delivery.status === 'In Transit' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {delivery.status}
                    </span>
                  </div>

                  {delivery.delivery_address && (
                    <p className="text-sm text-gray-600 mb-2">
                      <svg className="inline h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {delivery.delivery_address}
                    </p>
                  )}

                  {delivery.assignee && (
                    <p className="text-sm text-gray-600">
                      <svg className="inline h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {t('deliveries.assignedTo')}: {delivery.assignee.full_name}
                    </p>
                  )}

                  {delivery.notes && (
                    <p className="text-sm text-gray-500 mt-2 italic">{delivery.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="mt-2 text-sm text-gray-600">{t('deliveries.noDeliveries')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryCalendar;
